import { ref, watch } from "vue";

import { useStore as useUIStatesStore } from "@/store/stores/ui-states";
import { type EditorMode, isEditorMode } from "@/types/email";

interface UseEditorModeStateOptions {
  // Computes the starting mode when the URL doesn't pin one via `?editor_mode`.
  // May have side effects (e.g. defaulting the template) since it runs once.
  defaultMode: () => EditorMode;
  // Runs on every mode change, before persistence — e.g. to preserve scroll.
  onModeChange?: (mode: EditorMode) => void;
}

// Owns the editor's `mode` ref: seeds it from the `?editor_mode` URL override or
// the caller's default, and persists changes back to the user's saved editor
// mode (skipping naked, which is transient).
export function useEditorModeState({
  defaultMode,
  onModeChange,
}: UseEditorModeStateOptions) {
  const uiStates = useUIStatesStore();

  const urlMode = new URLSearchParams(window.location.search).get(
    "editor_mode"
  );

  const mode = ref<EditorMode>(isEditorMode(urlMode) ? urlMode : defaultMode());

  watch(mode, (next) => {
    onModeChange?.(next);

    // Never save naked mode as the persisted default.
    if (next !== "naked") {
      uiStates.editorMode = next;
    }
  });

  return mode;
}
